import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { get } from 'lodash'

import ErrorMessage from './ErrorMessage'
import { useFormContext } from '@context/FormContext'

const Radio = ({ 
  name, 
  label, 
  options, 
  maxColumns = 3, 
  autocomplete = 'off',
  withDescription = false,
  ...props 
}) => {
  const { values, errors, touched, submitCount, setFieldValue, setFieldError, setFieldTouched } = useFormContext()
  
  const fieldValue = get(values, name)
  const fieldError = get(errors, name)
  const isTouched = get(touched, name)
  const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
  
  // Only show error if:
  // 1. There's an actual error
  // 2. Field has been touched OR form has been submitted
  // 3. Field doesn't have a valid value (if it has a value, don't show error)
  const fieldTouched = !!fieldError && (isTouched || submitCount > 0) && !hasValue

  const handleChange = (e) => {
    const newValue = e.target.value
    
    try {
      // Mark field as touched when user selects a value
      setFieldTouched(name, true)
      // Update the field value
      setFieldValue(name, newValue)
      // Clear error immediately when a value is selected
      if (fieldError && newValue) {
        setFieldError(name, undefined)
      }
      props.onChange && props.onChange(newValue)
    } catch (error) {
      // Silently handle errors - they will be caught by error boundary if critical
    }
  }

  return (
    <fieldset
      className={classNames(
        'mb-4 border-0 p-0',
        { 'border-danger': fieldError && fieldTouched },
        { 'border-success': !fieldError && fieldTouched },
      )}
    >
      <legend
        className={classNames(
          'text-base font-semibold text-gray-900 mb-3',
          { 'sr-only': !label },
        )}
      >
        {label || name}
      </legend>

      <div className={classNames(
        'flex flex-wrap w-full',
        maxColumns === 1 && props.tightSpacing ? 'flex-col items-center gap-5 radio-options-tight' : 'flex-col items-start gap-5',
        maxColumns === 2 && 'grid grid-cols-1 md:grid-cols-2 gap-3',
        maxColumns === 3 && 'grid grid-cols-1 md:grid-cols-3 gap-3',
      )}
      >
        {options.map((option) => {
          const isScheduleTimeSelection = props.tightSpacing && maxColumns === 1
          return (
            <div
              key={option.value}
              className={classNames(
                'flex items-center',
                props.tightSpacing && maxColumns === 1 ? 'w-auto justify-center' : 'w-full',
                { 'opacity-60 cursor-not-allowed': props.disabled || option.disabled }
              )}
            >
              <label
                className={classNames(
                  'flex cursor-pointer group items-center',
                  props.tightSpacing && maxColumns === 1 ? 'justify-center' : 'w-full',
                  props.withDescription ? 'flex-row items-start' : '',
                  { 'cursor-not-allowed': props.disabled || option.disabled }
                )}
                htmlFor={`${name}_${option.value}`}
              >
                <input
                  autoComplete={autocomplete}
                  checked={String(fieldValue) === String(option.value)}
                  className={classNames(
                    'h-5 w-5 text-primary-orange focus:ring-2 focus:ring-primary-orange/20 focus:ring-offset-2 border-gray-3 cursor-pointer transition-colors flex-shrink-0',
                    props.withDescription ? 'mt-0.5' : '',
                    props.tightSpacing ? 'mr-1' : 'mr-3'
                  )}
                  disabled={props.disabled || option.disabled}
                  id={`${name}_${option.value}`}
                  name={name}
                  type="radio"
                  value={option.value}
                  onChange={handleChange}
                />
                <div className={classNames('flex flex-col', props.tightSpacing && maxColumns === 1 ? '' : 'flex-1')}>
                  <span className={classNames(
                    'text-base font-medium text-gray-900',
                    fieldValue === option.value && 'text-primary-orange font-semibold',
                    { 'text-gray-500': props.disabled || option.disabled }
                  )}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className={classNames(
                      'text-sm text-gray-600 mt-0.5',
                      { 'text-gray-400': props.disabled || option.disabled }
                    )}>
                      {option.description}
                    </span>
                  )}
                </div>
              </label>
            </div>
          )
        })}
      </div>

      <ErrorMessage render={!!(fieldError && fieldTouched)} message={fieldError} />
    </fieldset>
  )
}

Radio.propTypes = {
  name: PropTypes.string.isRequired,
  autocomplete: PropTypes.string,
  disabled: PropTypes.bool,
  spread: PropTypes.bool,
  label: PropTypes.string,
  maxColumns: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    label: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
    description: PropTypes.string,
  })).isRequired,
  withDescription: PropTypes.bool,
  tightSpacing: PropTypes.bool,
}

export default Radio
